import { useEffect } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";
import useRetailList from "../../hooks/useRetailList.hook";
import { MembershipPrice, ProductFormData } from "../../types/product";
import { RetailShopRes } from "../../types/retailShop";
import { LabelAndInput } from "../reuseables/form.comp";
import { EditingMode } from "../reuseables/form.comp";
import { Membership } from "../../types/membership";
import useMemberships from "../../hooks/useMemberships.hook";
import localeCore from "../../core/locale.core";

interface ProductPricesProps {
  methods: UseFormReturn<any>;
  editingMode: EditingMode;
}
export default function ProductPrices({ methods, editingMode }: ProductPricesProps) {
  const { t, msg } = useTranslation();
  const { data: retailShops } = useRetailList();
  const { data: memberships } = useMemberships();

  if (!retailShops) return null;
  if (!memberships) return null;

  //  render only after prices set in editingMode
  if (editingMode === EditingMode.edit && !methods.getValues(["retailPrices", "membershipPrices"])) {
    return null;
  }

  return (
    <section className="flex flex-col gap-4">
      <div className=" justify-stretch items-stretch flex gap-2">
        <LabelAndInput
          step=".01"
          type="number"
          register={methods.register}
          fieldName="price"
          topLabelCss="w-full block"
          registerOptions={{ min: 0 }}
          label={t(msg.product.price2)}
          errors={methods.formState.errors}
        />
      </div>

      <div className=" justify-stretch items-stretch flex gap-2">
        <LabelAndInput
          step=".01"
          type="number"
          register={methods.register}
          fieldName="eshopPrice"
          topLabelCss="w-full"
          registerOptions={{ min: 0 }}
          label={t(msg.product.eshopPrice)}
          errors={methods.formState.errors}
        />
      </div>

      <div className=" justify-stretch items-stretch flex gap-2">
        <LabelAndInput
          step=".01"
          type="number"
          register={methods.register}
          fieldName="discountPrice"
          topLabelCss="w-full"
          registerOptions={{ min: 0 }}
          label={t(msg.product.discountPrice)}
          errors={methods.formState.errors}
        />
      </div>

      <div className="justify-stretch items-stretch flex gap-2 mb-4">
        <LabelAndInput
          step=".01"
          type="number"
          register={methods.register}
          fieldName="defaultRetailPrice"
          topLabelCss="w-full"
          registerOptions={{ min: 0 }}
          label={t(msg.product.defaultRetailPrice)}
          errors={methods.formState.errors}
        />
      </div>
      <RetailPrices methods={methods} retailShops={retailShops} />
      <MembershipPrices methods={methods} memberships={memberships} />
    </section>
  );
}

interface RetailPricesProps {
  retailShops: RetailShopRes[];
  methods: UseFormReturn<ProductFormData, any>;
}
const RetailPrices = ({ retailShops, methods }: RetailPricesProps) => {
  const { t, msg } = useTranslation();

  const retailPrices = useWatch({
    name: "retailPrices",
    control: methods.control,
  });

  useEffect(() => {
    const prices = retailShops.map((s) => {
      const retailShop = retailPrices?.find((p) => p.retailShopId === s.id);
      return {
        retailShopId: s.id, //
        price: retailShop?.price ?? null,
      };
    });
    methods.setValue("retailPrices", prices as never);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="border p-4 mb-4">
      <div className="block text-base font-semibold mb-2">{t(msg.product.retailPrices)}</div>

      <div id="retail-price-list">
        <div className="flex flex-wrap gap-6">
          {retailPrices?.map((retailPrice, index) => {
            const shopName = retailShops.find((s) => s.id === retailPrice.retailShopId)?.name;
            return (
              <PriceRow
                key={retailPrice.retailShopId}
                methods={methods}
                idx={index}
                rowName={shopName}
                fieldName="retailPrices"
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface MembershipPricesProps {
  memberships: Membership[];
  methods: UseFormReturn<ProductFormData, any>;
}
const MembershipPrices = ({ memberships, methods }: MembershipPricesProps) => {
  const { t, msg } = useTranslation();

  const membershipPrices = useWatch({
    name: "membershipPrices",
    control: methods.control,
  });

  useEffect(() => {
    const prices: MembershipPrice[] = memberships.map((s) => {
      const membership = membershipPrices?.find((p) => p.membershipId === s.id);
      return {
        membershipId: s.id, //
        price: membership?.price ?? null,
      };
    });
    methods.setValue("membershipPrices", prices as never);
    // eslint-disable-next-line
  }, []);

  if (!membershipPrices) return null;

  return (
    <div className="border p-4 ">
      <div className="block text-base font-semibold mb-4">{t(msg.product.membershipPrices)}</div>

      <div id="retail-price-list">
        <div className="flex flex-wrap gap-6">
          {membershipPrices?.map((membershipPrice, index) => {
            const localeName = memberships.find((s) => s.id === membershipPrice.membershipId)?.name;
            const membershipName = localeCore.getLocaleValueDefault(localeName);
            return (
              <PriceRow
                key={membershipPrice.membershipId}
                methods={methods}
                idx={index}
                rowName={membershipName ?? ""}
                fieldName="membershipPrices"
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface PriceRowProps {
  methods: UseFormReturn<ProductFormData, any>;
  idx: number;
  fieldName: string;
  rowName?: string;
}
function PriceRow({ rowName, fieldName, methods, idx }: PriceRowProps) {
  const { t, msg } = useTranslation();

  return (
    <div className="items-center py-3 ">
      <h3>{rowName}</h3>
      <div className="flex gap-2">
        <LabelAndInput
          step="0.01"
          type="number"
          label={t(msg.product.price2)}
          register={methods.register}
          fieldName={`${fieldName}.${idx}.price`}
          topLabelCss="block"
          labelCss="block text-[10px] mb-1"
        />
      </div>
    </div>
  );
}
